import { useState, useEffect } from 'react';
import './Raffle.css';
import { apiService } from '../services/cloudflareAPI';

const Raffle = () => {
  const [activeRaffle, setActiveRaffle] = useState(null);
  const [winnersData, setWinnersData] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [countdown, setCountdown] = useState('');

  useEffect(() => {
    fetchRaffleData();
    // Poll for updates every 30 seconds
    const interval = setInterval(fetchRaffleData, 30000);
    return () => clearInterval(interval);
  }, []);

  // Countdown timer effect
  useEffect(() => {
    if (!activeRaffle?.end_time || !activeRaffle?.start_time) return;

    const updateCountdown = () => {
      const now = Math.floor(Date.now() / 1000); // Current time in Unix seconds

      // Check if raffle hasn't started yet
      if (now < activeRaffle.start_time) {
        const timeUntilStart = activeRaffle.start_time - now;
        const days = Math.floor(timeUntilStart / (60 * 60 * 24));
        const hours = Math.floor((timeUntilStart % (60 * 60 * 24)) / (60 * 60));
        const minutes = Math.floor((timeUntilStart % (60 * 60)) / 60);
        const seconds = timeUntilStart % 60;

        if (days > 0) {
          setCountdown(`Starts in ${days}d ${hours}h ${minutes}m ${seconds}s`);
        } else if (hours > 0) {
          setCountdown(`Starts in ${hours}h ${minutes}m ${seconds}s`);
        } else {
          setCountdown(`Starts in ${minutes}m ${seconds}s`);
        }
        return;
      }

      // Calculate time until end
      const distance = activeRaffle.end_time - now;

      if (distance <= 0) {
        setCountdown('ENDED');
        return;
      }

      const days = Math.floor(distance / (60 * 60 * 24));
      const hours = Math.floor((distance % (60 * 60 * 24)) / (60 * 60));
      const minutes = Math.floor((distance % (60 * 60)) / 60);
      const seconds = distance % 60;

      if (days > 0) {
        setCountdown(`${days}d ${hours}h ${minutes}m ${seconds}s`);
      } else if (hours > 0) {
        setCountdown(`${hours}h ${minutes}m ${seconds}s`);
      } else {
        setCountdown(`${minutes}m ${seconds}s`);
      }
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);

    return () => clearInterval(interval);
  }, [activeRaffle]);

  const fetchRaffleData = async () => {
    setLoading(true);
    setError(null);

    try {
      const [raffleInfo, winnersInfo, statsInfo] = await Promise.all([
        apiService.getActiveRaffle().catch(err => {
          console.warn('Active raffle endpoint not available:', err);
          return null;
        }),
        apiService.getRaffleWinners().catch(err => {
          console.warn('Winners endpoint not available:', err);
          return null;
        }),
        apiService.getRaffleStats().catch(err => {
          console.warn('Stats endpoint not available:', err);
          return null;
        })
      ]);

      // Handle different response formats
      const processedRaffleInfo = raffleInfo?.data || raffleInfo;

      if (processedRaffleInfo) {
        console.log('Prize Pool (bonus_pool):', processedRaffleInfo.bonus_pool);
        console.log('Prize Pool (prize_pool):', processedRaffleInfo.prize_pool);
        console.log('Participants (participation_count):', processedRaffleInfo.participation_count);
        console.log('Participants (participants):', processedRaffleInfo.participants);
        console.log('All raffle fields:', Object.keys(processedRaffleInfo));
      }
      console.log('=========================');

      setActiveRaffle(processedRaffleInfo);
      setWinnersData(winnersInfo?.data || winnersInfo);
      setStats(statsInfo?.data || statsInfo);
    } catch (err) {
      console.error('Error fetching raffle data:', err);
      setError(`Failed to load raffle data: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const formatDateTime = (unixTimestamp, isoFallback) => {
    if (!unixTimestamp && !isoFallback) return 'N/A';

    let date;
    if (unixTimestamp) {
      date = new Date(unixTimestamp * 1000); // Convert Unix timestamp to milliseconds
    } else if (isoFallback) {
      date = new Date(isoFallback); // Parse ISO 8601 string
    }

    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  const renderActiveRaffle = () => {
    const hashtag = activeRaffle?.hashtag?.startsWith('#')
      ? activeRaffle.hashtag
      : activeRaffle?.hashtag
      ? `#${activeRaffle.hashtag}`
      : null;

    return (
      <div className="raffle-active">
        <div className="raffle-main-info">
          <h2 className="raffle-title">Active Raffle</h2>
          {hashtag && (
            <div className="raffle-hashtag">HashTag {hashtag}</div>
          )}
        </div>

        <div className="raffle-info-grid">
          <div className="raffle-info-card">
            <span className="info-label">Start Time</span>
            <span className="info-value">
              {activeRaffle
                ? formatDateTime(activeRaffle.start_time, activeRaffle.formatted_times?.start_time_iso)
                : 'TBA'}
            </span>
          </div>

          {countdown && (
            <div className="raffle-info-card countdown-card">
              <span className="info-label">Countdown</span>
              <span className="info-value countdown-value">{countdown}</span>
            </div>
          )}

          <div className="raffle-info-card prize-card">
            <span className="info-label">Prize Pool</span>
            <span className="info-value prize-value">{activeRaffle?.prize_pool || 'TBA'}</span>
          </div>

          <div className="raffle-info-card">
            <span className="info-label">Participants</span>
            <span className="info-value">{activeRaffle?.participants ?? 'TBA'}</span>
          </div>
        </div>

        {activeRaffle?.status && (
          <div className={`raffle-status status-${activeRaffle.status.toLowerCase()}`}>
            STATUS: {activeRaffle.status.toUpperCase()}
          </div>
        )}
      </div>
    );
  };

  const renderWinners = () => {
    // Check if raffle has ended and has participants but no winners yet
    const raffleEnded = activeRaffle?.status === 'ended' ||
                        (activeRaffle?.end_time && activeRaffle.end_time < Math.floor(Date.now() / 1000));
    const hasParticipants = activeRaffle?.participants > 0;

    // Always show winners section if we have winners data (from completed raffles)
    if (!winnersData || !winnersData.winners || winnersData.winners.length === 0) {
      // If raffle ended with participants, show waiting message
      if (raffleEnded && hasParticipants) {
        return (
          <div className="winners-section">
            <div className="no-data">
              <h3>Recent Winners</h3>
              <p>Waiting for winners to be selected...</p>
            </div>
          </div>
        );
      }

      return (
        <div className="winners-section">
          <div className="no-data">
            <h3>Recent Winners</h3>
            <p>No winners available yet</p>
          </div>
        </div>
      );
    }

    // Display winners from the most recent completed raffle
    const hashtag = winnersData.hashtag?.startsWith('#')
      ? winnersData.hashtag
      : `#${winnersData.hashtag}`;

    return (
      <div className="winners-section">
        <h2>Recent Winners - {hashtag}</h2>
        <div className="winners-info">
          <span>Total Prize Pool: {winnersData.total_prize_pool}</span>
          <span>Total Winners: {winnersData.total_winners}</span>
        </div>
        <div className="winners-list">
          {winnersData.winners.map((winner, index) => (
            <div key={winner.telegram_id || index} className="winner-item">
              <div className="winner-rank">
                #{index + 1}
              </div>
              <div className="winner-info">
                <div className="winner-username">
                  {winner.telegram_username || `User_${winner.telegram_id}`}
                </div>
                <div className="winner-prize">
                  Prize: {winner.prize}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderStats = () => {
    if (!stats) {
      return (
        <div className="stats-section">
          <div className="no-data">
            <h3>Global Statistics</h3>
            <p>No statistics available</p>
          </div>
        </div>
      );
    }

    return (
      <div className="stats-section">
        <h2>Global Raffle Statistics</h2>
        <div className="stats-grid">
          {stats.total_raffles !== undefined && (
            <div className="stat-card">
              <div className="stat-value">{stats.total_raffles}</div>
              <div className="stat-label">Total Raffles</div>
            </div>
          )}

          {stats.total_winners !== undefined && (
            <div className="stat-card">
              <div className="stat-value">{stats.total_winners}</div>
              <div className="stat-label">Total Winners</div>
            </div>
          )}

          {stats.total_participants !== undefined && (
            <div className="stat-card">
              <div className="stat-value">{stats.total_participants}</div>
              <div className="stat-label">Total Participants</div>
            </div>
          )}

          {stats.total_prize_value && (
            <div className="stat-card">
              <div className="stat-value">{stats.total_prize_value}</div>
              <div className="stat-label">Total Prizes Awarded</div>
            </div>
          )}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="raffle-container">
        <div className="loading">Loading raffle information...</div>
      </div>
    );
  }

  return (
    <div className="raffle-container">
      <div className="raffle-header-section">
        <h1>Raffle</h1>
        <p className="raffle-subtitle">Participate in raffles and win amazing prizes!</p>
      </div>

      {error && (
        <div className="error-message">
          <span>{error}</span>
          <button onClick={fetchRaffleData} className="retry-btn-small">
            Retry
          </button>
        </div>
      )}

      <div className="raffle-content">
        {renderActiveRaffle()}
        {renderWinners()}
        {renderStats()}
      </div>
    </div>
  );
};

export default Raffle;
